import React from "react";
import { UseCompanyColors } from "../../hooks/company";
import { GET_IMAGE } from "../../helpers/images";

/* ── colour helpers: derive pedestal shades from the company brand colour ── */
const hexToRgb = (hex) => {
  let h = (hex || "#1B598E").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
};
const toHex = (r, g, b) => {
  const c = (v) => `0${Math.max(0, Math.min(255, Math.round(v))).toString(16)}`.slice(-2);
  return `#${c(r)}${c(g)}${c(b)}`;
};
const mix = (hex, target, amt) => {
  const a = hexToRgb(hex);
  const b = hexToRgb(target);
  return toHex(a.r + (b.r - a.r) * amt, a.g + (b.g - a.g) * amt, a.b + (b.b - a.b) * amt);
};
const lighten = (h, a) => mix(h, "#ffffff", a);
const darken = (h, a) => mix(h, "#000000", a);

/* Medal treatment for the top-three photo frames + their laurels (fixed). */
const MED = [
  { ring: "linear-gradient(135deg,#FBE9A8,#E6B400 48%,#A9820A)", leaf: "#C79A2E", pts: "#9A7A12" },
  { ring: "linear-gradient(135deg,#EFF2F6,#B8BEC9 48%,#868E9C)", leaf: "#9AA3B0", pts: "#697380" },
  { ring: "linear-gradient(135deg,#F0C89A,#CD7F32 48%,#8F5218)", leaf: "#B5701F", pts: "#8F5218" },
];

const ini = (n) => {
  const p = (n || "").split(" ").filter(Boolean);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "•";
};

const esc = (str) =>
  String(str == null ? "" : str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const laurel = (sz, color) => {
  const lv = [[30, 98, 35, 9, 4], [21, 80, 58, 9, 4], [16, 60, 82, 9, 4], [19, 41, 108, 8.5, 3.8], [29, 26, 130, 7.5, 3.4], [42, 16, 150, 6.5, 3]];
  const branch = (m) => {
    let g = `<g${m ? ' transform="translate(120,0) scale(-1,1)"' : ""}>`;
    g += `<path d="M60 108 C34 104,16 84,16 58 C16 40,26 24,44 14" fill="none" stroke="${color}" stroke-width="2" opacity="0.5"/>`;
    lv.forEach((l, i) => { g += `<ellipse cx="${l[0]}" cy="${l[1]}" rx="${l[3]}" ry="${l[4]}" fill="${color}" opacity="${0.5 + i * 0.09}" transform="rotate(${l[2]} ${l[0]} ${l[1]})"/>`; });
    return `${g}</g>`;
  };
  return `<svg width="${sz}" height="${sz}" viewBox="0 0 120 120" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);">${branch(false)}${branch(true)}</svg>`;
};

const g5 = (id, e, m, h) => `<linearGradient id="${id}" x1="0" x2="1"><stop offset="0" stop-color="${e}"/><stop offset="0.16" stop-color="${m}"/><stop offset="0.48" stop-color="${h}"/><stop offset="0.82" stop-color="${m}"/><stop offset="1" stop-color="${e}"/></linearGradient>`;
const g3 = (id, a, b) => `<linearGradient id="${id}" x1="0" x2="1"><stop offset="0" stop-color="${a}"/><stop offset="0.5" stop-color="${b}"/><stop offset="1" stop-color="${a}"/></linearGradient>`;

const cyl = (bh, num, fs, r, s) => {
  const VW = 150;
  const CW = 126;
  const offX = (VW - CW) / 2;
  const ryc = 11;
  const ryb = 15;
  const colTopY = 15;
  const colBot = colTopY + bh;
  const baseTopY = colBot;
  const baseH = 26;
  const baseBot = baseTopY + baseH;
  const H = baseBot + ryb + 8;
  return `<svg width="100%" viewBox="0 0 ${VW} ${H}" style="display:block;"><defs>`
    + g5(`bd${r}`, s.edge, s.mid, s.hi) + g3(`ctp${r}`, s.ctpA, s.ctpB) + g3(`ccb${r}`, s.ccbA, s.ccbB)
    + g5(`bbd${r}`, s.bbdEdge, s.bbdMid, s.bbdHi) + g3(`btp${r}`, s.btpA, s.btpB) + g3(`bbt${r}`, s.bbtA, s.bbtB) + `</defs>`
    + `<ellipse cx="${VW / 2}" cy="${baseBot + ryb + 3}" rx="${VW * 0.46}" ry="6" fill="rgba(16,24,40,0.16)"/>`
    + `<rect x="0" y="${baseTopY}" width="${VW}" height="${baseH}" fill="url(#bbd${r})"/>`
    + `<ellipse cx="${VW / 2}" cy="${baseBot}" rx="${VW / 2}" ry="${ryb}" fill="url(#bbt${r})"/>`
    + `<ellipse cx="${VW / 2}" cy="${baseTopY}" rx="${VW / 2}" ry="${ryb}" fill="url(#btp${r})" stroke="${s.rim}" stroke-width="1"/>`
    + `<rect x="${offX}" y="${colTopY}" width="${CW}" height="${bh}" fill="url(#bd${r})"/>`
    + `<ellipse cx="${VW / 2}" cy="${colBot}" rx="${CW / 2}" ry="${ryc}" fill="url(#ccb${r})"/>`
    + `<ellipse cx="${VW / 2}" cy="${colTopY}" rx="${CW / 2}" ry="${ryc}" fill="url(#ctp${r})" stroke="${s.rim}" stroke-width="1.2"/>`
    + `<text x="${VW / 2}" y="${(colTopY + bh / 2 + fs * 0.34).toFixed(1)}" text-anchor="middle" font-family="DM Sans, sans-serif" font-size="${fs}" font-weight="800" fill="#fff">${num}</text></svg>`;
};

// k: uniform scale factor so the podium can live in narrower homes (the
// dashboard's podium block) without overflowing. Text sizes are clamped so
// names and points stay legible even when the artwork shrinks.
// pedestal: multiplies cylinder heights only — lets a tall card get a tall
// podium without inflating the avatars/frames.
const colHTML = (team, rank, s, k = 1, pedestal = 1) => {
  const m = MED[rank];
  const big = rank === 0;
  const wrap = Math.round((big ? 124 : 100) * k);
  const av = Math.round((big ? 66 : 54) * k);
  let bh = Math.round(58 * k * pedestal); // rank 2 — the shortest column
  if (big) bh = Math.round(96 * k * pedestal);
  else if (rank === 1) bh = Math.round(70 * k * pedestal);
  const fs = Math.round((big ? 42 : 34) * k);
  const nameFs = Math.max(10.5, 12.5 * k);
  const ptsFs = Math.max(10, 12 * k);
  const inner = av - Math.max(8, Math.round(11 * k));
  // Resolve the stored image key to a full URL (same as SoftProfile) — the
  // podium draws a raw <img>, so an unresolved key would 404 to a broken icon.
  const imgSrc = team.image ? GET_IMAGE(team.image) : null;
  const face = imgSrc
    ? `<img src="${esc(imgSrc)}" alt="" style="width:100%;height:100%;border-radius:50%;object-fit:cover;"/>`
    : `<span style="font-weight:700;color:#667085;font-size:${inner * 0.36}px;">${esc(ini(team.name))}</span>`;
  return `<div style="position:relative;width:${wrap}px;height:${wrap}px;display:flex;align-items:center;justify-content:center;">`
    + laurel(wrap, m.leaf)
    + `<div style="width:${av}px;height:${av}px;border-radius:50%;background:${m.ring};display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(16,24,40,0.14);">`
    + `<div style="width:${inner}px;height:${inner}px;border-radius:50%;background:#fff;overflow:hidden;display:flex;align-items:center;justify-content:center;box-shadow:inset 0 1px 2px rgba(0,0,0,0.12);">${face}</div></div></div>`
    + `<div class="gmb-podium-name" style="font-family:DM Sans;font-size:${nameFs}px;font-weight:600;color:#101828;margin-top:6px;text-align:center;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(team.name)}</div>`
    + `<div class="gmb-podium-pts" style="font-family:DM Sans;font-size:${ptsFs}px;font-weight:700;color:${m.pts};">${(team.points || 0).toLocaleString()} pts</div>`
    + `<div style="width:100%;margin-top:${Math.round(10 * k)}px;">${cyl(bh, rank + 1, fs, rank, s)}</div>`;
};

const Podium = ({ top3 = [], scale = 1, pedestal = 1 }) => {
  const { primaryColor } = UseCompanyColors() || {};
  const brand = primaryColor || "#1B598E";

  // Self-fit: the three columns span ~496px at scale 1 (200k + 2×172k − 2×24k
  // overlap). Whatever scale the caller asks for, never exceed what the
  // measured container can hold — callers with fixed scales (Company View
  // passes none → 1) were clipping on laptop widths.
  const fitRef = React.useRef(null);
  const [fitW, setFitW] = React.useState(0);
  React.useEffect(() => {
    const el = fitRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      if (entries[0]) setFitW(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const effScale = fitW ? Math.min(scale, (fitW * 0.97) / 496) : scale;
  const s = {
    edge: darken(brand, 0.42), mid: brand, hi: lighten(brand, 0.34),
    ctpA: lighten(brand, 0.12), ctpB: lighten(brand, 0.42),
    ccbA: darken(brand, 0.5), ccbB: darken(brand, 0.28),
    bbdEdge: darken(brand, 0.55), bbdMid: darken(brand, 0.12), bbdHi: lighten(brand, 0.18),
    btpA: darken(brand, 0.02), btpB: lighten(brand, 0.3),
    bbtA: darken(brand, 0.6), bbtB: darken(brand, 0.38),
    rim: lighten(brand, 0.4),
  };
  const [first, second, third] = top3;
  const k = effScale;
  const cell = (team, rank, style) =>
    team ? <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", width: Math.round((rank === 0 ? 200 : 172) * k), ...style }} dangerouslySetInnerHTML={{ __html: colHTML(team, rank, s, k, pedestal) }} /> : null;

  return (
    <div ref={fitRef} style={{ width: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      {cell(second, 1, { zIndex: 1, transform: `translateY(${Math.round(-15 * k)}px)`, marginRight: `${Math.round(-24 * k)}px` })}
      {cell(first, 0, { zIndex: 3 })}
      {cell(third, 2, { zIndex: 1, transform: `translateY(${Math.round(-15 * k)}px)`, marginLeft: `${Math.round(-24 * k)}px` })}
    </div>
  );
};

export default Podium;
